import React from 'react';
import { CircularProgress, Alert, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WifiIcon from '@mui/icons-material/Wifi';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WarningIcon from '@mui/icons-material/Warning';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifiOff';
import { useFixLogs } from '../../hooks/useFixLogs';
import { useSignalRContext } from '../../SignalR/useSignalR';
import './Monitoring.css';

const LATENCY_COLORS = {
  green: '#22c55e',
  yellow: '#facc15',
  red: '#ef4444',
  cyan: '#06b6d4',
};

function getLatencyColor(latency) {
  if (latency > 100) return 'red';
  if (latency > 80) return 'yellow';
  if (latency < 0) return 'cyan';
  return 'green';
}

const Monitoring = () => {
  // Use the dynamic FIX log hook (now from SignalR)
  const { metrics, alerts, isConnected, loading } = useFixLogs();
  // Debug log for data flow
  // Keep useSignalRContext for dataSource, isMockMode, etc.
  const { 
    dataSource, 
    isMockMode, 
    mockDataWarning, 
    connectionQuality, 
    lastRealDataUpdate,
    connectionHealth 
  } = useSignalRContext();

  // NEW: Data source indicator
  const getDataSourceIndicator = () => {
    if (dataSource === "real") {
      return {
        icon: <CheckCircleIcon />,
        color: "#22c55e",
        text: "LIVE DATA",
        bgColor: "rgba(34, 197, 94, 0.1)"
      };
    } else if (dataSource === "mock") {
      return {
        icon: <WarningIcon />,
        color: "#f59e0b",
        text: "CACHED DATA",
        bgColor: "rgba(245, 158, 11, 0.1)"
      };
    } else {
      return {
        icon: <SignalWifiOffIcon />,
        color: "#ef4444",
        text: "DISCONNECTED",
        bgColor: "rgba(239, 68, 68, 0.1)"
      };
    }
  };

  const dataSourceInfo = getDataSourceIndicator();

  // Latency chart: last 12 values, fallback to 0s if missing
  const latencyHistory = metrics && metrics.latency && Array.isArray(metrics.latency.history)
    ? metrics.latency.history.slice(-12)
    : Array(12).fill(0);
  const latencyCurrent = metrics && metrics.latency ? metrics.latency.current : '--';
  const latencyAvg = metrics && metrics.latency ? metrics.latency.average : '--';
  const latencyMax = metrics && metrics.latency ? metrics.latency.max : '--';
  const latencyColor = typeof latencyCurrent === 'number' ? getLatencyColor(latencyCurrent) : 'cyan';

  // Heartbeat status
  let heartbeatStatus = '--';
  if (metrics && metrics.heartbeat) {
    if (metrics.heartbeat === 'healthy') heartbeatStatus = 'OK';
    else if (metrics.heartbeat === 'delayed') heartbeatStatus = 'LATE';
    else if (metrics.heartbeat === 'missing') heartbeatStatus = 'MISSING';
    else heartbeatStatus = metrics.heartbeat;
  }

  // Orders processed
  const ordersProcessed = metrics && typeof metrics.ordersProcessed === 'number' ? metrics.ordersProcessed : '--';

  // Uptime
  const uptime = metrics && metrics.uptime ? metrics.uptime : '--:--:--';

  // Session info
  const serverName = metrics && metrics.serverName ? metrics.serverName : 'cServer.TRADE';
  const sessionId = metrics && metrics.sessionId ? metrics.sessionId : '--';
  const lastExec = metrics && metrics.lastExecTime ? metrics.lastExecTime : '--:--';
  const sequence = metrics && metrics.sequence ? metrics.sequence : '--';

  // System alerts
  const systemAlerts = Array.isArray(alerts) ? alerts : [];

  // Footer metrics
  const perMinute = metrics && typeof metrics.messagesPerMinute === 'number' ? metrics.messagesPerMinute : '--';
  const errorRate = metrics && typeof metrics.errorRate === 'number' ? metrics.errorRate : '--';

  // Loading/empty state
  if (loading) {
    return (
      <div className="monitoring-scrollable">
        <div className="monitoring-card" style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:300}}>
          <CircularProgress color="info" size={36} />
          <span style={{marginLeft:16,fontWeight:600}}>Connecting...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="monitoring-scrollable">
      <div className="monitoring-card">
        {/* Data Source Alert */}
        {(mockDataWarning && !isMockMode) && (
          <Alert 
            severity="warning" 
            icon={dataSourceInfo.icon}
            sx={{
              mb: 2,
              backgroundColor: dataSourceInfo.bgColor,
              border: `1px solid ${dataSourceInfo.color}`,
              '& .MuiAlert-icon': {
                color: dataSourceInfo.color
              },
              '& .MuiAlert-message': {
                color: dataSourceInfo.color,
                fontWeight: 600
              }
            }}
          >
            {dataSourceInfo.text} - {mockDataWarning ? "Connection restored" : ""}
          </Alert>
        )}

        {/* Header */}
        <div className="monitoring-header">
          <div className="monitoring-header-left">
            <ShowChartIcon className="monitoring-header-icon" />
            <span>FIX Engine</span>
            {isConnected && dataSource === "real" && (
              <CheckCircleIcon className="monitoring-header-status" />
            )}
          </div>
          <div className="monitoring-header-latency" style={{fontSize: '1.1rem', fontWeight: 400}}>
            <span style={{color: LATENCY_COLORS[latencyColor]}}>
              {latencyCurrent !== undefined && latencyCurrent !== null && latencyCurrent !== '--' ? latencyCurrent : '--'}
              <span style={{fontSize: '1.15rem', fontWeight: 700, marginLeft: 4}}>ms</span>
            </span>
          </div>
        </div>

        {/* Metric Grid */}
        <div className="monitoring-metric-grid">
          <div className="monitoring-metric-box">
            <div className="monitoring-metric-label" style={{fontSize: '1.0rem', fontWeight: 800}}>Order latency</div>
            <div className={`monitoring-metric-value ${latencyColor}`} style={{fontSize: '1.1rem', fontWeight: 400}}>
              {latencyCurrent !== undefined && latencyCurrent !== null && latencyCurrent !== '--' ? latencyCurrent : '--'}
              <span style={{fontSize: '1.15rem', fontWeight: 700, marginLeft: 4}}>ms</span>
            </div>
            <div className="monitoring-metric-sub" style={{fontSize: '1.08rem', fontWeight: 400, whiteSpace: 'nowrap'}}>Avg: {latencyAvg}ms | Max: {latencyMax}ms</div>
          </div>
          <div className="monitoring-metric-box">
            <div className="monitoring-metric-label" style={{fontSize: '1.0rem', fontWeight: 800}}>Heartbeat</div>
            <div className={`monitoring-metric-value ${heartbeatStatus === 'OK' ? 'green' : heartbeatStatus === 'LATE' ? 'yellow' : heartbeatStatus === 'MISSING' ? 'red' : 'cyan'}`} style={{fontSize: '1.1rem', fontWeight: 400}}>{heartbeatStatus}</div>
          </div>
          <div className="monitoring-metric-box">
            <div className="monitoring-metric-label" style={{fontSize: '1.0rem', fontWeight: 800}}>Orders</div>
            <div className="monitoring-metric-value cyan" style={{fontSize: '1.1rem', fontWeight: 400}}>{ordersProcessed}</div>
          </div>
          <div className="monitoring-metric-box">
            <div className="monitoring-metric-label" style={{fontSize: '1.0rem', fontWeight: 800}}>Uptime</div>
            <div className="monitoring-metric-value green" style={{fontSize: '1.1rem', fontWeight: 400}}>{uptime}</div>
          </div>
        </div>

        {/* Latency Chart */}
        <div className="monitoring-latency-chart">
          <div className="monitoring-latency-chart-title-row">
            <span style={{fontSize: '1.13rem', fontWeight: 800}}>Real-time Latency</span>
            <span className="monitoring-latency-chart-value" style={{fontSize: '1.1rem', fontWeight: 400}}>
              {latencyCurrent !== undefined && latencyCurrent !== null && latencyCurrent !== '--' ? latencyCurrent : '--'}
              <span style={{fontSize: '1.05rem', fontWeight: 700, marginLeft: 4}}>ms</span>
            </span>
          </div>
          <div className="monitoring-latency-bars">
            {latencyHistory.map((v, i) => {
              const barClass = v > 80 ? 'red' : 'green';
              return (
                <div
                  key={i}
                  className={`monitoring-latency-bar ${barClass}`}
                  style={{height: Math.max(8, Math.min(38, v/2))}}
                  title={v + 'ms'}
                />
              );
            })}
          </div>
          <div className="monitoring-latency-chart-labels">
            <span>-36s</span>
            <span>Now</span>
          </div>
        </div>

        {/* Session Info */}
        <div className="monitoring-session-2x2">
          <div className="session-cell server-cell">
            <WifiIcon className="monitoring-session-icon" />
            <span className="server-name">{serverName}</span>
          </div>
          <div className="session-cell sessionid-cell">
            <span className="session-label">Session:</span>
            <span className="session-value">{sessionId}</span>
          </div>
          <div className="session-cell lastexec-cell">
            <span className="session-label">Last Exec:</span>
            <span className="session-value">{lastExec}</span>
          </div>
          <div className="session-cell seq-cell">
            <span className="session-label">Seq:</span>
            <span className="session-value">{sequence}</span>
          </div>
        </div>

        {/* System Alerts Section */}
        <div className="system-alerts-card">
          <div className="system-alerts-title">System Alerts</div>
          <div className="system-alerts-list card-style">
            {/* Always show all possible alert types, with N/A if not present */}
            {(() => {
              // Define all possible alert types
              const possibleAlerts = [
                { key: 'latencySpike', label: 'Latency spike detected' },
                { key: 'positionReport', label: 'Position report updated' },
                { key: 'highLatency', label: 'High latency' },
                { key: 'latencyWarning', label: 'Latency warning' },
              ];
              // Map alert keys to detection logic
              const alertMatchers = {
                latencySpike: alert => alert.message && alert.message.toLowerCase().includes('latency spike'),
                positionReport: alert => alert.message && alert.message.toLowerCase().includes('position report'),
                highLatency: alert => alert.message && alert.message.toLowerCase().includes('high latency'),
                latencyWarning: alert => alert.message && alert.message.toLowerCase().includes('latency warning'),
                connectionLost: alert => alert.message && alert.message.toLowerCase().includes('connection lost'),
                heartbeatDelay: alert => alert.message && alert.message.toLowerCase().includes('heartbeat delay'),
              };
              return possibleAlerts.map(({ key, label }) => {
                const found = systemAlerts.find(alert => alertMatchers[key](alert));
                if (found) {
                  return (
                    <div className="system-alert-row" key={key}>
                      <span className="system-alert-icon">
                        {found.type === 'red' ? (
                          <WarningIcon style={{ color: '#ef4444', fontSize: 20 }} />
                        ) : found.type === 'yellow' ? (
                          <WarningIcon style={{ color: '#facc15', fontSize: 20 }} />
                        ) : found.type === 'position' ? (
                          <CheckCircleIcon style={{ color: '#38bdf8', fontSize: 20 }} />
                        ) : (
                          <WarningIcon style={{ color: '#a0aec0', fontSize: 20 }} />
                        )}
                      </span>
                      <span className="system-alert-message">{label}</span>
                      <span className="system-alert-time">{found.time}</span>
                    </div>
                  );
                } else {
                  return (
                    <div className="system-alert-row" key={key} style={{opacity: 0.6, fontWeight: 400}}>
                      <span className="system-alert-icon">
                        <WarningIcon style={{ color: '#a0aec0', fontSize: 20 }} />
                      </span>
                      <span className="system-alert-message">{label}</span>
                      <span className="system-alert-time">N/A</span>
                    </div>
                  );
                }
              });
            })()}
          </div>
        </div>

        {/* Footer */}
        <div className="monitoring-footer">
          <span><AccessTimeIcon className="footer-icon" />Messages: {perMinute}/min</span>
          <span className="footer-error"><ErrorOutlineIcon className="footer-icon" />Errors: {errorRate}%</span>
        </div>
      </div>
    </div>
  );
};

export default Monitoring; 